import { FC, useCallback, useMemo, useState } from "react";

import { rollbarAndLogError } from "../../../../../utils/logger.ts";

import { AssessmentSummaryReportMenu } from "./AssessmentSummaryReportMenu";
import { AssessmentSummaryTable } from "./AssessmentSummaryTable";

import {
  serverGetFullAssessmentData,
  serverGetPageAssessmentSummary,
  serverGetSectionAssessmentSummary,
} from "api/api-server-data";
import { CourseMenu } from "components/menus/CourseMenu";
import { SectionMenu } from "components/menus/SectionMenu";
import { TermMenu } from "components/menus/TermMenu";
import { t } from "i18n/i18n";
import { Listbox } from "mds/components/Listbox";
import { ORDER_BY_TITLE } from "mds/hooks/use-ordered.ts";
import { useAppSelector } from "store/index";
import { courseSelectors, termSelectors } from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";
import { toastErrorMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { downloadCSVFile } from "utils/file";
import { useCourseIdsInTerm } from "utils/hooks/use-course-ids-in-term";
import {
  generateAssessmentDetailCSV,
  generateFullAssessmentCSV,
  generateSectionAssessmentSummaryCSV,
} from "utils/reporting";

type DateRangeOption = {
  id: string;
  title: string;
  value: number;
};

const DATE_RANGE_OPTIONS: DateRangeOption[] = [
  { id: "7", title: t("common.date_range.last_7_days"), value: 7 },
  { id: "30", title: t("common.date_range.last_30_days"), value: 30 },
  { id: "all", title: t("common.date_range.all_time"), value: -1 },
];

export const AssessmentSummaryReport: FC = () => {
  const [selectedTermId, setSelectedTermId] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption>(
    () => DATE_RANGE_OPTIONS.find((option) => option.id === "30")!,
  );
  const selectedTermTitle = useAppSelector(
    (state) => termSelectors.selectById(state, selectedTermId)?.title,
  );
  const selectedCourseCode = useAppSelector(
    (state) => courseSelectors.selectById(state, selectedCourseId)?.code,
  );
  const getCourseIdsInTerm = useCourseIdsInTerm();
  const courseIdsInTerm = useMemo(
    () => getCourseIdsInTerm(selectedTermId),
    [getCourseIdsInTerm, selectedTermId],
  );

  const handleTermChange = (termId: string) => {
    setSelectedTermId(termId);
    setSelectedSectionId("");
    if (!getCourseIdsInTerm(termId).has(selectedCourseId)) setSelectedCourseId("");
  };

  const handleCourseChange = (courseId: string) => {
    setSelectedCourseId(courseId);
    setSelectedSectionId("");
  };

  // Calculate date range based on selected option
  const getDateRange = useCallback(() => {
    const today = new Date();
    const endDate = today.toISOString().split("T")[0]; // YYYY-MM-DD format

    if (selectedDateRange.value === -1) {
      // All time - use a date far in the past
      return {
        startDate: "2020-01-01",
        endDate,
      };
    }

    const startDate = new Date(today);
    startDate.setDate(today.getDate() - selectedDateRange.value);
    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate,
    };
  }, [selectedDateRange.value]);

  const { data, isLoading, error } = usePromiseEffect(async () => {
    const { startDate, endDate } = getDateRange();

    if (!selectedTermId) {
      return [];
    }

    return serverGetSectionAssessmentSummary({
      term_id: selectedTermId,
      start_date: startDate,
      end_date: endDate,
    });
  }, [selectedTermId, getDateRange]);

  // We will allow sorting any dashboard table by any column, but
  // making this table sort by course code then course title.
  const sortedData = useMemo(() => {
    if (!data) return undefined;

    if (selectedSectionId) {
      return data.filter((item) => item.id === selectedSectionId);
    }

    if (selectedCourseCode) {
      return data.filter((item) => item.course_code === selectedCourseCode).sort(ORDER_BY_TITLE);
    }

    return [...data].sort((a, b) => {
      const courseComparison = a.course_code.localeCompare(b.course_code);
      if (courseComparison !== 0) {
        return courseComparison;
      }

      // eslint-disable-next-line babel/new-cap
      return ORDER_BY_TITLE(a, b);
    });
  }, [data, selectedCourseCode, selectedSectionId]);

  const handleSummaryReport = () => {
    if (!selectedTermId) {
      toastErrorMessage(t("assessment_summary_report.select_term_to_view_data"));
      return;
    }

    if (!sortedData || sortedData.length === 0) {
      toastErrorMessage(t("assessment_summary_report.no_data"));
      return;
    }

    try {
      const { startDate, endDate } = getDateRange();
      const termTitle = selectedTermTitle || selectedTermId;

      const { csvData: summaryCsvData, filename: summaryFilename } =
        generateSectionAssessmentSummaryCSV(sortedData, termTitle, { startDate, endDate });
      trackEvent("Home - Reports / Download assessment report", {
        ...BUTTON_EVENT_CATEGORY,
        fileName: summaryFilename,
      });
      downloadCSVFile(summaryCsvData, summaryFilename);
    } catch (exc) {
      rollbarAndLogError("Error in handleSummaryReport", exc);
      toastErrorMessage(t("assessment_summary_report.export_failed"));
    }
  };

  const handleDetailedReport = async () => {
    if (!selectedTermId) {
      toastErrorMessage(t("assessment_summary_report.select_term_to_view_data"));
      return;
    }

    try {
      const { startDate, endDate } = getDateRange();
      const termTitle = selectedTermTitle || selectedTermId;

      const pageData = await serverGetPageAssessmentSummary({
        term_id: selectedTermId,
        course_id: selectedCourseId || undefined,
        section_id: selectedSectionId || undefined,
        start_date: startDate,
        end_date: endDate,
      });
      const { csvData: detailedCsvData, filename: detailedFilename } = generateAssessmentDetailCSV(
        pageData,
        termTitle,
        { startDate, endDate },
      );
      trackEvent("Home - Reports / Download assessment report", {
        ...BUTTON_EVENT_CATEGORY,
        fileName: detailedFilename,
      });
      downloadCSVFile(detailedCsvData, detailedFilename);
    } catch (exc) {
      rollbarAndLogError("Error in handleDetailedReport", exc);
      toastErrorMessage(t("assessment_summary_report.export_failed"));
    }
  };

  const handleFullReport = async () => {
    if (!selectedTermId) {
      toastErrorMessage(t("assessment_summary_report.select_term_to_view_data"));
      return;
    }

    try {
      const { startDate, endDate } = getDateRange();
      const termTitle = selectedTermTitle || selectedTermId;

      const fullAssessmentData = await serverGetFullAssessmentData({
        term_id: selectedTermId,
        course_id: selectedCourseId || undefined,
        section_id: selectedSectionId || undefined,
        start_date: startDate,
        end_date: endDate,
      });
      const { csvData: fullCsvData, filename: fullFilename } = generateFullAssessmentCSV(
        fullAssessmentData,
        termTitle,
        { startDate, endDate },
      );
      trackEvent("Home - Reports / Download assessment report", {
        ...BUTTON_EVENT_CATEGORY,
        fileName: fullFilename,
      });
      downloadCSVFile(fullCsvData, fullFilename);
    } catch (exc) {
      rollbarAndLogError("Error in handleFullReport", exc);
      toastErrorMessage(t("assessment_summary_report.export_failed"));
    }
  };

  return (
    <div className="assessment-summary-view">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="h3">{t("assessment_summary_report.title")}</h2>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <TermMenu
            defaultToAll={false}
            eventPrefix="Home - Reports / "
            selectedTermId={selectedTermId}
            size="s"
            onTermChange={handleTermChange}
          />
        </div>

        <div className="flex items-center gap-2">
          <CourseMenu
            disabled={!selectedTermId}
            filterBy={(c) => courseIdsInTerm.has(c.id)}
            selectedCourseId={selectedCourseId}
            size="s"
            adminAccessibleOnly
            onCourseChange={handleCourseChange}
          />
        </div>

        <div className="flex items-center gap-2">
          <SectionMenu
            courseId={selectedCourseId || null}
            disabled={!selectedTermId}
            emptyOptionText={t("import_modal.all_sections")}
            selectedSectionId={selectedSectionId}
            size="s"
            termId={selectedTermId || null}
            onSectionChange={setSelectedSectionId}
          />
        </div>

        <div className="flex items-center gap-2">
          <Listbox
            buttonSize="s"
            disabled={!selectedTermId}
            eventName="Home - Reports / Time period selected"
            eventPropertyKey="timePeriod"
            items={DATE_RANGE_OPTIONS}
            selectedValue={selectedDateRange.id}
            onSelectionChange={(item) => setSelectedDateRange(item)}
          />
        </div>

        <div className="flex items-center gap-2">
          <AssessmentSummaryReportMenu
            disabled={!selectedTermId}
            onDetailedReport={handleDetailedReport}
            onFullReport={handleFullReport}
            onSummaryReport={handleSummaryReport}
          />
        </div>
      </div>

      {/* Error message */}
      {error && <div className="bg-red-50 text-red-700 mb-4 rounded-md p-4">{error.message}</div>}

      {/* Loading state */}
      {isLoading && (
        <div className="mt-8 text-center text-black-tint-40">{t("common.loading")}...</div>
      )}

      {/* Table */}
      {sortedData && <AssessmentSummaryTable data={sortedData} />}

      {/* No data message */}
      {sortedData && !error && sortedData.length === 0 && (
        <div className="mt-4 text-black-tint-40">
          {selectedTermId
            ? t("assessment_summary_report.no_data")
            : t("assessment_summary_report.select_term_to_view_data")}
        </div>
      )}
    </div>
  );
};
